import React from 'react';
import {FormDialog, Button, RaymanUser, UserSize} from 'components/core';
import FilesList from 'components/files/FilesList';
import {TasksCtx, TaskFilesCtx, TaskEdit} from 'components/dataProviders/tasks';
import {UsersCtx} from 'components/dataProviders/users';
import {ProceduresCtx} from 'components/dataProviders/procedures';
import {Add} from '@material-ui/icons';
import {TagsCtx} from 'components/dataProviders/tags';
import { makeStyles } from '@material-ui/core/styles';
import {SelectorItem} from 'components/core/Form';
import TaskDatePickerField from 'components/tasks/TaskDatePickerField';

const useStyles = makeStyles((theme) => ({
  dialog:
  {
    width: 700
  }
}), {name: "EditTaskDialog"});

interface EditTaskDialogProps {
  open: boolean,
  handleClose: () => void,
  handleConfirm: () => void,
  taskID?: number
}

const priorityItems = [
  {
    value: 'low',
    content: "Baja"
  },
  {
    value: 'normal',
    content: "Normal"
  },
  {
    value: 'important',
    content: "Importante"
  },
  {
    value: 'urgent',
    content: "Urgente"
  }
]
export default function EditTaskDialog({open, handleClose=()=>{}, handleConfirm=()=>{}, taskID} : EditTaskDialogProps)
{
    const classes = useStyles();
  
    const mainCtx = React.useContext(TasksCtx);
    const tagsCtx = React.useContext(TagsCtx);
    const filesCtx = React.useContext(TaskFilesCtx);
    const procsCtx = React.useContext(ProceduresCtx);
    const usersCtx = React.useContext(UsersCtx);

    const task = taskID ? mainCtx.data.indexed[taskID] : {asignees: [], name: "", priority: "normal", tags: [], minStartDate: null, maxStartDate: new Date().toISOString(), info: ""};

    const [loading, setLoading] = React.useState(false);
    const [tagItems , setTagItems]  = React.useState<SelectorItem[]>([]);
    const [userItems, setUserItems] = React.useState<SelectorItem[]>([]);
    const [procedureItems, setProcedureItems] = React.useState<SelectorItem[]>([]);
    

    React.useEffect(function initializeTags() {
      let tags : SelectorItem[] = [];
      tagsCtx.data.active.forEach((item : {tagID: number, name: string}) => tags.push({value: item.tagID, content: item.name}));

      setTagItems(tags);
    }, [tagsCtx]);  

    React.useEffect(function InitializeUsers() {
      let operators : SelectorItem[] = [];

      usersCtx.data.profiles.operators.forEach((item : {userID: number}) => {
        operators.push(
          {
            value: item.userID,
            content: <RaymanUser userID={item.userID} size={UserSize.size24} />
          }
        )
      });
      setUserItems(operators);

    }, [usersCtx]);

    React.useEffect(function InitializeProcs() {

      let procs : SelectorItem[] = [];
      procsCtx.data.array.forEach((item : {procedureID: number, name: string}) => procs.push({value: item.procedureID, content: item.name}));
      setProcedureItems(procs);

    }, [procsCtx]);


    const onConfirm = (values : TaskEdit & {taskDate?: {minStartDate: string, maxStartDate: string}}) => {
      setLoading(true);
      if(values.taskDate)
      {
        values.minStartDate = values.taskDate.minStartDate !== null ? new Date(values.taskDate.minStartDate) : null; 
        values.maxStartDate = values.taskDate.maxStartDate !== null ? new Date(values.taskDate.maxStartDate) : null;
      }
      delete values.taskDate;
      if(taskID) // edit mode
      {
        mainCtx.funcs.update(taskID, values).then(() => {
          setLoading(false);
          handleConfirm();
          handleClose();
        });
      }
      else // create mode
      {
        mainCtx.funcs.create(values).then(() => {
          setLoading(false);
          handleConfirm();
          handleClose();
        });
      }
    }

    const uploadFile = React.useCallback(() =>
    {
        filesCtx.funcs.upload();
    }, [filesCtx]);

    const procedureField = {
        type: "selector",
        label: "Seleccionar procedimiento",
        id: "procedureID",
        mandatory: true,
        value: '',
        items: procedureItems
    };

    const filesField = {
        type: "custom",
        label: "Archivos",
        id: "files",
        content: <><FilesList context={TaskFilesCtx} editable/><Button fullWidth={false} color="primary" onClick={()=>uploadFile()}><Add />Añadir Archivo</Button></>
    };
    
    const formFields = [
      {
          type: "multiselector",
          label: "Asignar a",
          id: "asignees",
          value: task.asignees,
          items: userItems
      },
      {
        type: "text",
        label: "Nombre",
        id: "name",
        placeholder: "Nombre de la tarea",
        mandatory: true,
        value: task.name
      },
      {
        type: "selector",
        label: "Prioridad",
        id: "priority",
        mandatory: true,
        value: task.priority,
        items: priorityItems
      },
      {
        type: "tagSelector",
        label: "Añadir etiquetas",
        id: "tags",
        value: task.tags,
        items: tagItems
      },
      {
        type: "custom",
        label: "Fecha",
        value: {minStartDate: task.minStartDate, maxStartDate: task.maxStartDate},
        id: "taskDate",
        managed: true,
        content: TaskDatePickerField
      },
      {
        type: "textarea",
        label: "Información Adicional",
        id: "info",
        placeholder: "",
        mandatory: false,
        value: task.info
      }
    ];

    return <FormDialog
      open={open}
      onClose={handleClose}
      onConfirm={onConfirm}
      title={taskID ? "Editar tarea" : "Nueva tarea"}
      subtitle="Rellena los campos con toda la información de la tarea"
      confirm="Guardar"
      cancel="Cancelar"
      loading={loading}
      className={classes.dialog}
      fields={taskID ? [...formFields, filesField] : [procedureField, ...formFields]}
      />
}
